<template>
  <div>
    <preview-cv
      :data-to-show="isPreviewPopupDisplayed"
      :cv-to-preview="form"
      @close="isPreviewPopupDisplayed = false"
    />

    <div
      class="hidden relative xl:flex items-center bg-indigo-200 overflow-visible rounded-sm h-48 w-full"
    >
      <div class="px-12">
        <p class="text-4xl text-gray-900">Curriculum vitae</p>
        <p
          class="mt-2 text-base font-light leading-4 2xl:leading-5 text-gray-600 max-w-2/3 2xl:max-w-11/12"
        >
          Mettez en avant vos parcours et compétences en créant un CV
          personnalisé à l'aide de notre outil de création.
        </p>
      </div>
      <img
        src="/assets/img/Illustration-curriculum-vitae.png"
        alt="CV"
        class="hidden xl:block absolute right-5 xl:right-10 -top-8 w-60 xl:w-72"
      />
    </div>

    <action-button
      v-if="isFormPage"
      class="w-max my-5 lg:my-15 xl:hidden"
      border-color="border-transparent"
      bg-color="bg-indigo-300"
      bg-color-square="bg-indigo-100"
      icon-color="text-indigo-600"
      text-color="text-gray-900"
      icon="expand"
      libelle="Aperçu"
      @clickAction="isPreviewPopupDisplayed = true"
    />

    <div
      class="xl:mt-15 grid grid-cols-11"
      :class="{ 'xl:gap-x-10': isFormPage }"
    >
      <div
        class="align-middle shadow rounded-lg border-b border-gray-200 bg-white pb-10 table min-h-halfscreen w-full"
        :class="{
          'col-span-full': !isFormPage,
          'col-span-full xl:col-span-7': isFormPage,
        }"
      >
        <router-view @downloadPDF="downloadPDF" />
      </div>
      <div
        v-show="isFormPage"
        class="hidden col-span-4 max-h-screen xl:flex flex-col"
      >
        <div class="flex justify-between items-center">
          <h4 class="font-light text-gray-600 text-left">Prévisualisation</h4>
          <div class="flex space-x-1.5">
            <div
              id="fullscreen"
              class="bg-indigo-100 border-4 border-indigo-300 rounded-md p-1.5 cursor-pointer transform hover:scale-110 transition duration-300"
              @click="isPreviewPopupDisplayed = true"
            >
              <icon name="expand" class="w-4 h-4 text-indigo-600" />
            </div>
            <div
              @click="refreshPage"
              id="refresh-button"
              class="bg-teal-100 border-4 border-teal-300 rounded-md p-1.5 cursor-pointer transform hover:scale-110 transition duration-300"
            >
              <icon name="refresh" class="w-4 h-4 text-teal-600" />
            </div>
          </div>
        </div>
        <div v-if="!displayPreview" class="flex justify-center mt-15">
          <div
            class="rounded-full w-4 h-4 border-t-2 border-r-2 border-l-0 border-b-0 border-gray-900 border-opacity-25 animate-spin"
          />
        </div>
        <div
          class="hidden xl:inline-block transform origin-top-left mt-5 z-0 min-h-pdf"
          :class="{
            'xl:scale-35 xxl:scale-40 2xl:scale-50': isPortraitOrientation,
            'xl:scale-25 xxl:scale-30 2xl:scale-40': !isPortraitOrientation,
          }"
        >
          <vue-html2pdf
            v-if="displayPreview"
            id="containerVueHtml2PDF"
            class="bg-white w-max border-2"
            :class="{
              'border-gray-600': pdfHeight <= pdfHeightMax,
              'border-pink-600': pdfHeight > pdfHeightMax,
            }"
            :show-layout="false"
            :float-layout="false"
            :enable-download="true"
            :preview-modal="false"
            :manual-pagination="false"
            :paginate-elements-by-height="pdfHeightMax + 1"
            :pdf-content-width="!isPortraitOrientation ? '1122px' : '800px'"
            :html-to-pdf-options="{
              enableLinks: true,
              filename: 'CV ' + username,
              jsPDF: {
                orientation: isPortraitOrientation ? 'portrait' : 'landscape',
                format: 'a4',
              },
              html2canvas: {
                scale: 2,
                useCORS: true,
              },
            }"
            ref="cvPdf"
          >
            <section slot="pdf-content">
              <section class="pdf-item">
                <model-pdf
                  :model-name="(((form || {}).data || {}).shaping || {}).name"
                  :model-color="(((form || {}).data || {}).shaping || {}).color"
                  :form="form"
                />
              </section>
            </section>
          </vue-html2pdf>
        </div>
        <div
          @click="SET_CURRENTSTEP(cvPages[0].name)"
          class="-mt-136 2xl:-mt-130 2xl:mr-10"
        >
          <div
            v-if="pdfHeight > pdfHeightMax"
            class="flex space-x-2 items-center justify-center text-center text-base font-light text-pink-600"
          >
            <icon name="alert-triangle" class="text-pink-600" />
            <div>Le contenu dépasse la taille de la page</div>
          </div>
          <p
            class="text-center text-base font-light text-indigo-600 cursor-pointer"
          >
            Modifier le modèle
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueHtml2pdf from 'vue-html2pdf';
import { mapGetters, mapMutations } from 'vuex';
import ModelPdf from './../curriculumVitae/pdf/ModelPdf.vue';
import Constants from './../../constants';
import Icon from './../../components/Icon.vue';
import ActionButton from '../../components/ActionButton.vue';

import PreviewCv from './../curriculumVitae/popup/PreviewCv.vue';

export default {
  name: 'CvLayout',

  components: {
    VueHtml2pdf,
    ModelPdf,
    Icon,
    PreviewCv,
    ActionButton,
  },

  data() {
    return {
      isPreviewPopupDisplayed: false,
      pdfHeightMax: 1122,
      constants: Constants,
    };
  },

  mounted() {
    if (document.getElementById('containerVueHtml2PDF')) {
      this.SET_PDFHEIGHT(
        document.getElementById('containerVueHtml2PDF').clientHeight
      );
    }
  },

  updated() {
    if (document.getElementById('containerVueHtml2PDF')) {
      this.SET_PDFHEIGHT(
        document.getElementById('containerVueHtml2PDF').clientHeight
      );
    }
  },

  methods: {
    ...mapMutations({
      SET_CURRENTSTEP: 'curriculumVitae/SET_CURRENTSTEP',
      SET_PDFHEIGHT: 'curriculumVitae/SET_PDFHEIGHT',
      SET_DOWNLOADING: 'curriculumVitae/SET_DOWNLOADING',
      SET_DISPLAYPREVIEW: 'curriculumVitae/SET_DISPLAYPREVIEW',
    }),

    async downloadPDF() {
      this.SET_DOWNLOADING(true);
      await this.$refs.cvPdf.generatePdf();

      this.SET_DOWNLOADING(false);
    },

    refreshPage() {
      this.SET_DISPLAYPREVIEW(false);
      setTimeout(() => {
        this.SET_DISPLAYPREVIEW(true);
      }, 1000);
    },
  },

  computed: {
    ...mapGetters({
      form: 'curriculumVitae/form',
      currentStep: 'curriculumVitae/currentStep',
      pdfHeight: 'curriculumVitae/pdfHeight',
      displayPreview: 'curriculumVitae/displayPreview',
      downloading: 'curriculumVitae/downloading',
    }),

    isFormPage() {
      return (
        this.$route.path.includes('/curriculumvitae/form/') &&
        this.currentStep != 'shaping'
      );
    },

    cvPages() {
      return Constants.CV_PAGES[this.form.type];
    },

    username() {
      return this.form.data.personnal
        ? this.form.data.personnal.prenom.value +
            ' ' +
            this.form.data.personnal.nom.value
        : '';
    },

    isPortraitOrientation() {
      switch (this.form.type) {
        case Constants.CV_TYPES.BASIC:
          return true;

        case Constants.CV_TYPES.DASHBOARD:
          return false;

        default:
          return true;
      }
    },
  },
};
</script>
